import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Header.css';
import siteConfig from '../../configuration/siteConfiguration';

const DO_CHECK_ON_GLOBAL_MOUSE_CLICK = false;
const CURRENT_VIEW_IS_MOBILE = window.innerWidth < 768;

const assetUrl = `${siteConfig.assetBaseUrl}/icons/verticals-we-power/default`

// Navigation links configuration
const navLinks = [
  {
    label: 'Home',
    to: '/',
    type: 'link',
  },
  {
    label: 'Products',
    type: 'dropdown',
    dropdownKey: 'cipProducts',  // ✅ New dropdown
    subLinks: [
      {
        label: 'Cognitva CIP™',
        to: '/cip',
        contentClass: 'dropdown-item-content-submenu',
      },
      {
        label: 'Ideonix™',
        to: 'https://web.ideonix.ai/',
        contentClass: 'dropdown-item-content-submenu',
      },
    ],
  },
  {
    label: 'Verticals we power',
    to: '/verticals-we-power',
    icon: '/images/verticalswepower.svg',
    type: 'link'
  },
  {
    label: 'Who we are',
    type: 'dropdown',
    dropdownKey: 'whoWeAre',
    subLinks: [
      {
        label: 'About Us',
        to: '/about-us',
        icon: '/images/aboutus.svg',
        iconClass: 'legal-icon-submenu',
        contentClass: 'dropdown-item-content-submenu',
      },
      {
        label: 'Careers',
        to: '/careers',
        icon: '/images/careers.svg',
        hiring: true,
        iconClass: 'legal-icon-submenu',
        contentClass: 'dropdown-item-content-submenu',
      },
    ],
  },
  {
    label: 'Contact',
    to: '/contact',
    type: 'link',
  },
];

const Header = () => {
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCipProductsOpen, setIsCipProductsOpen] = useState(false);  // ✅ New state
  const [isWhoWeAreOpen, setIsWhoWeAreOpen] = useState(false);

  const cipProductsRef = useRef(null);  // ✅ New ref
  const whoWeAreRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY === 0) {
        setIsVisible(true);
      }
      else if (currentScrollY > lastScrollY && currentScrollY > 50) {
        setIsVisible(false);
        if (isMobileMenuOpen) {
          setIsMobileMenuOpen(false);
        }
        setIsCipProductsOpen(false);  // ✅ Close on scroll
        setIsWhoWeAreOpen(false);
      }
      else if (currentScrollY < lastScrollY) {
        setIsVisible(true);
      }

      setLastScrollY(currentScrollY);
    };

    let ticking = false;
    const handleScrollThrottled = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScrollThrottled, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScrollThrottled);
    };
  }, [lastScrollY, isMobileMenuOpen]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (CURRENT_VIEW_IS_MOBILE && !DO_CHECK_ON_GLOBAL_MOUSE_CLICK) return;
      
      let targetElement = event?.target;
      const isInside = (ref) => {
        while (targetElement) {
          if (ref.current && ref.current === targetElement) {
            return true;
          }
          targetElement = targetElement.parentNode;
        }
        return false;
      };

      // ✅ Check CIP Products dropdown
      targetElement = event.target;
      const isInsideCipProductsRef = isInside(cipProductsRef);
      
      targetElement = event.target;
      const isInsideWhoWeAreRef = isInside(whoWeAreRef);
      
      if (cipProductsRef.current && !isInsideCipProductsRef) {
        setIsCipProductsOpen(false);
      }

      if (whoWeAreRef.current && !isInsideWhoWeAreRef) {
        setIsWhoWeAreOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleMobileNavClick = (path, e) => {
    e?.stopPropagation();
    e?.preventDefault();

    if (path) {
      window.location.href = path;
    }
  };

  // ✅ New toggle function for CIP Products
  const toggleCipProducts = () => {
    setIsCipProductsOpen(!isCipProductsOpen);
    setIsWhoWeAreOpen(false);
  };

  const toggleWhoWeAre = () => {
    setIsWhoWeAreOpen(!isWhoWeAreOpen);
    setIsCipProductsOpen(false);  // ✅ Close CIP Products when opening Who We Are
  };

  const NavLinkClass = (path) => {
    const currentPath = location.pathname.toLowerCase();
    if (!path) return 'nav-item';
    return currentPath === path.toLowerCase() ? 'nav-item nav-item-current' : 'nav-item';
  }

  return (
    <>
      <header className={`header ${isVisible ? 'header-visible' : 'header-hidden'}`}>
        <div className="header-container">
          {/* Desktop Layout */}
          <div className="desktop-layout">
            <div className="logo-section">
              <Link to="/" >
                <img src="companylogo.svg" alt="Cognitva.ai logo" className="logo-full" />
              </Link>
            </div>
            <div className="right-section">
              <nav className="navigation">
                {navLinks.map((nav, idx) => {
                  if (nav.type === 'link') {
                    return (
                      <Link key={nav.label} to={nav.to} className={NavLinkClass(nav.to)}>{nav.label}</Link>
                    );
                  }
                  
                  // ✅ CIP Products Dropdown
                  if (nav.type === 'dropdown' && nav.dropdownKey === 'cipProducts') {
                    return (
                      <div className={`${NavLinkClass(nav.to)} dropdown`} ref={cipProductsRef} key={nav.label}>
                        <div className="dropdown-trigger" onClick={toggleCipProducts}>
                          <span>{nav.label}</span>
                          <svg
                            className={`dropdown-icon ${isCipProductsOpen ? 'rotated' : ''}`}
                            width="12"
                            height="8"
                            viewBox="0 0 12 8"
                          >
                            <path d="M1 1L6 6L11 1" stroke="currentColor" strokeWidth="2" fill="none" />
                          </svg>
                        </div>
                        {isCipProductsOpen && (
                          <div className="dropdown-menu">
                            {nav.subLinks.map((sub, subIdx) => (
                              <Link
                                to={sub.to}
                                className="dropdown-item"
                                onClick={() => setIsCipProductsOpen(false)}
                                key={sub.label}
                              >
                                {sub.icon && (
                                  <div className="dropdown-item-icon">
                                    <img src={sub.icon} alt={sub.label} className="dropdown-svg-icon" />
                                  </div>
                                )}
                                <div className="dropdown-item-content">
                                  <div className="dropdown-item-title">{sub.label}</div>
                                </div>
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  }
                  
                  // Who We Are Dropdown (unchanged)
                  if (nav.type === 'dropdown' && nav.dropdownKey === 'whoWeAre') {
                    return (
                      <div className={`${NavLinkClass(nav.to)} dropdown`} ref={whoWeAreRef} key={nav.label}>
                        <div className="dropdown-trigger" onClick={toggleWhoWeAre}>
                          <span>{nav.label}</span>
                          <svg
                            className={`dropdown-icon ${isWhoWeAreOpen ? 'rotated' : ''}`}
                            width="12"
                            height="8"
                            viewBox="0 0 12 8"
                          >
                            <path d="M1 1L6 6L11 1" stroke="currentColor" strokeWidth="2" fill="none" />
                          </svg>
                        </div>
                        {isWhoWeAreOpen && (
                          <div className="dropdown-menu">
                            {nav.subLinks.map((sub, subIdx) => (
                              <Link
                                to={sub.to}
                                className="dropdown-item"
                                onClick={() => setIsWhoWeAreOpen(false)}
                                key={sub.label}
                              >
                                <div className="dropdown-item-icon">
                                  <img src={sub.icon} alt={sub.label} className="dropdown-svg-icon" />
                                </div>
                                <div className="dropdown-item-content">
                                  <div className="dropdown-item-title">
                                    {sub.label} {sub.hiring && <span className="hiring-badge">WE'RE HIRING</span>}
                                  </div>
                                </div>
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  }
                  return null;
                })}
              </nav>
              <button className="cta-nav-item" onClick={() => window.location.href = '/contact'}>
                Request for demo
              </button>
            </div>
          </div>

          {/* Mobile Layout */}
          <div className="mobile-layout">
            <div className="mobile-logo-section">
              <Link to="/" ><img src="companylogo.svg" alt="Cognitva.ai logo" className="logo-mobile" /></Link>
            </div>
            <div className="mobile-center-section">
              <button className="mobile-cta-button" onClick={() => window.location.href = '/contact'}>
                Request for demo
              </button>
            </div>
            <button className="mobile-menu-button" onClick={toggleMobileMenu}>
              <div className={`hamburger ${isMobileMenuOpen ? 'open' : ''}`}>
                <span></span>
                <span></span>
                <span></span>
              </div>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="mobile-menu-overlay">
          <div className="mobile-navigation">
            {navLinks.map((nav, idx) => {
              if (nav.type === 'link') {
                return (
                  <Link key={nav.label} to={nav.to}>
                    <div className="mobile-nav-item" onClick={() => handleMobileNavClick(nav.to)}>{nav.label}</div>
                  </Link>
                );
              }
              
              // ✅ Mobile CIP Products Dropdown
              if (nav.type === 'dropdown' && nav.dropdownKey === 'cipProducts') {
                return (
                  <React.Fragment key={nav.label}>
                    <div className="mobile-nav-item mobile-dropdown" onClick={toggleCipProducts} ref={cipProductsRef}>
                      <span>{nav.label}</span>
                      <svg
                        className={`dropdown-icon ${isCipProductsOpen ? 'rotated' : ''}`}
                        width="12"
                        height="8"
                        viewBox="0 0 12 8"
                      >
                        <path d="M1 1L6 6L11 1" stroke="currentColor" strokeWidth="2" fill="none" />
                      </svg>
                    </div>
                    {isCipProductsOpen && (
                      <div className="mobile-dropdown-menu">
                        {nav.subLinks.map((sub) => (
                          <Link
                            to={sub.to}
                            className="mobile-dropdown-item"
                            key={sub.label}
                            onClick={(e) => handleMobileNavClick(sub.to, e)}
                          >
                            {sub.icon && (
                              <div className={`dropdown-item-icon${sub.iconClass ? ' ' + sub.iconClass : ''}`}>
                                <img height={32} src={sub.icon} alt={sub.label} className="dropdown-svg-icon" />
                              </div>
                            )}
                            <div className={sub.contentClass || 'dropdown-item-content'}>
                              <div className="dropdown-item-title">{sub.label}</div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    )}
                  </React.Fragment>
                );
              }
              
              // Mobile Who We Are Dropdown (unchanged)
              if (nav.type === 'dropdown' && nav.dropdownKey === 'whoWeAre') {
                return (
                  <React.Fragment key={nav.label}>
                    <div className="mobile-nav-item mobile-dropdown" onClick={toggleWhoWeAre} ref={whoWeAreRef}>
                      <span>{nav.label}</span>
                      <svg
                        className={`dropdown-icon ${isWhoWeAreOpen ? 'rotated' : ''}`}
                        width="12"
                        height="8"
                        viewBox="0 0 12 8"
                      >
                        <path d="M1 1L6 6L11 1" stroke="currentColor" strokeWidth="2" fill="none" />
                      </svg>
                    </div>
                    {isWhoWeAreOpen && (
                      <div className="mobile-dropdown-menu">
                        {nav.subLinks.map((sub, subIdx) => (
                          <Link
                            to={sub.to}
                            className="mobile-dropdown-item"
                            key={sub.label}
                            onClick={(e) => handleMobileNavClick(sub.to, e)}
                          >
                            <div className={`dropdown-item-icon${sub.iconClass ? ' ' + sub.iconClass : ''}`}>
                              <img height={32} src={sub.icon} alt={sub.label} className="dropdown-svg-icon" />
                            </div>
                            <div className={sub.contentClass || 'dropdown-item-content'}>
                              <div className="dropdown-item-title">{sub.label}</div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    )}
                  </React.Fragment>
                );
              }
            })}
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
